import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronDown, Star, Play, Instagram, Youtube, Facebook,
  Clock, Mic2, Calendar, Newspaper, ExternalLink,
} from "lucide-react";
import FloatingEditButton from "@/components/FloatingEditButton";
import CarouselSection from "@/components/CarouselSection";
import ServiceFlipCard from "@/components/ServiceFlipCard";
import EnrollmentModal from "@/components/EnrollmentModal";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section, { SectionHeading } from "@/components/ui/Section";
import Card, { CardBody } from "@/components/ui/Card";
import CTAButton, { ArrowLink } from "@/components/ui/CTAButton";
import SmartImage from "@/components/ui/SmartImage";
import { CardGridSkeleton, Skeleton } from "@/components/ui/Skeleton";
import TikTok from "@/components/icons/TikTok";
import { normalizeImgUrl } from "@/lib/utils-img";
import { cargarPortada } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";
import useReducedMotion from "@/hooks/useReducedMotion";
import useParallax from "@/hooks/useParallax";

// ─── Ayudas visuales compartidas ──────────────────────────────────────────────

// Contador animado (IntersectionObserver + requestAnimationFrame).
// Extrae el número inicial de cadenas como "50.000+" u "85+" y conserva
// cualquier prefijo o sufijo no numérico.
function useCountUp(value, duration = 1600) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(String(value ?? ""));
  const reducido = useReducedMotion();

  useEffect(() => {
    const raw = String(value ?? "");
    const match = raw.match(/[\d.,]+/);
    if (!match) { setDisplay(raw); return; }
    const numStr = match[0];
    const target = parseInt(numStr.replace(/[.,]/g, ""), 10);
    if (!Number.isFinite(target)) { setDisplay(raw); return; }

    // Con movimiento reducido se muestra la cifra final sin recorrerla. El CSS
    // no puede frenar esto porque la animación la lleva JavaScript.
    if (reducido) { setDisplay(raw); return; }

    const prefix = raw.slice(0, match.index);
    const suffix = raw.slice(match.index + numStr.length);
    const el = ref.current;
    if (!el) { setDisplay(raw); return; }

    let frame;
    const fmt = n => prefix + n.toLocaleString("es-ES") + suffix;
    setDisplay(fmt(0));

    const run = () => {
      const start = performance.now();
      const tick = now => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        setDisplay(fmt(Math.round(target * eased)));
        if (t < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };

    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { run(); obs.disconnect(); } },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => { obs.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [value, duration, reducido]);

  return [ref, display];
}

// ─── Renderizadores de sección ────────────────────────────────────────────────

function HeroSection({ content }) {
  const c = content || {};
  const bgRef = useParallax(0.28);
  const imagen = c.bg_image ? normalizeImgUrl(c.bg_image) : null;
  const hasMedia = Boolean(c.bg_video || imagen);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: "#1d1d1f" }}
    >
      {/* Capa de fondo separada del contenido para poder desplazarla sola.
          Se dibuja algo más alta que la sección (scale en useParallax) para
          que al moverse no descubra un borde vacío por abajo. */}
      <div
        ref={bgRef}
        className="absolute inset-0 will-change-transform"
        style={
          imagen && !c.bg_video
            ? {
                backgroundImage: `url(${imagen})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
      >
        {c.bg_video && (
          <video autoPlay muted loop playsInline className="w-full h-full object-cover">
            <source src={c.bg_video} />
          </video>
        )}
      </div>

      {hasMedia && (
        <div className="absolute inset-0 bg-black" style={{ opacity: c.overlay_opacity ?? 0.55 }} />
      )}

      <div className="relative z-10 text-center text-white px-6 max-w-5xl mx-auto">
        <div className="flex justify-center mb-8">
          <img
            src="/logos/logo-icono.png"
            alt="Logo Evangelización Digital"
            className="h-[64px] md:h-[88px] w-auto object-contain animate-fadeIn"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </div>
        <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.3em] text-[#86868b] mb-8 animate-fadeIn">
          {c.subtitle || "Delegación de Evangelización Digital · Diócesis de Huelva"}
        </p>
        <h1
          className="font-heading font-bold text-white mb-7 animate-fadeInUp text-balance"
          style={{ fontSize: "var(--text-display)", lineHeight: 1.04, letterSpacing: "-0.03em" }}
        >
          {c.title || "Evangelizar en el mundo digital"}
          {c.title_italic && (
            <><br /><span className="font-spiritual italic font-normal text-white/60">{c.title_italic}</span></>
          )}
        </h1>
        <p className="text-lg sm:text-2xl text-[#a1a1a6] mb-12 max-w-2xl mx-auto leading-snug animate-fadeInUp animation-delay-200">
          {c.description || "Llevamos a Cristo a las redes, a las pantallas y a los corazones"}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp animation-delay-400">
          {c.cta_primary_text && (
            <CTAButton to={c.cta_primary_url || "/quienes-somos"} variant="light" size="lg"
              data-testid="hero-btn-conocenos">
              {c.cta_primary_text}
            </CTAButton>
          )}
          {c.cta_secondary_text && (
            <CTAButton to={c.cta_secondary_url || "/misionero-digital"} variant="outlineLight" size="lg"
              data-testid="hero-btn-missionary">
              {c.cta_secondary_text}
            </CTAButton>
          )}
        </div>
      </div>

      <a
        href="#about"
        aria-label="Ir al contenido"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hover:text-white transition-colors animate-bounce-down focus-ring-light rounded-full p-2"
      >
        <ChevronDown className="w-6 h-6" aria-hidden="true" />
      </a>
    </section>
  );
}

function MarqueeSection({ content }) {
  const c = content || {};
  const texto = c.text || "COMUNIÓN • MISIÓN • SERVICIO • INNOVACIÓN DIGITAL • EVANGELIZACIÓN";
  return (
    <div className="py-6 overflow-hidden" style={{ backgroundColor: "#1d1d1f" }} aria-hidden="true">
      <div className="flex whitespace-nowrap animate-marquee">
        {Array(4).fill(null).map((_, i) => (
          <span key={i} className="text-sm font-light px-8" style={{ color: "#86868b", letterSpacing: "0.2em" }}>
            {texto} &nbsp;•&nbsp;
          </span>
        ))}
      </div>
    </div>
  );
}

function TextImageSection({ content, tone }) {
  const c = content || {};
  const imgRight = c.image_position !== "left";
  const statValue = c.stat_value;

  const textBlock = (
    <div className="max-w-xl">
      {c.label && (
        <Reveal delay={0} distance={20}>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary mb-4">{c.label}</p>
        </Reveal>
      )}
      <Reveal delay={70} distance={36}>
        <h2 className="font-heading font-bold text-foreground mb-6 text-balance"
          style={{ fontSize: "var(--text-h2)", lineHeight: 1.08, letterSpacing: "-0.03em" }}>
          {c.title}
        </h2>
      </Reveal>
      {c.text1 && (
        <Reveal delay={140} distance={24}>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">{c.text1}</p>
        </Reveal>
      )}
      {c.text2 && (
        <Reveal delay={190} distance={24}>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">{c.text2}</p>
        </Reveal>
      )}
      {c.link_text && c.link_url && (
        <Reveal delay={240} distance={16}>
          <ArrowLink to={c.link_url} data-testid="home-about-link" className="text-base">
            {c.link_text} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </ArrowLink>
        </Reveal>
      )}
    </div>
  );

  // Columna derecha: la cifra destacada, a gran tamaño.
  const statBlock = (
    <div className={`flex flex-col ${imgRight ? "md:items-end md:text-right" : "md:items-start"}`}>
      {c.image && (
        <Reveal delay={120} distance={40} className="w-full">
          <SmartImage src={c.image} alt="" ratio="4 / 3" rounded="rounded-3xl" className="w-full mb-8 shadow-rest" />
        </Reveal>
      )}
      {statValue && (
        <Reveal delay={200} distance={28}>
          <p className="font-heading font-bold text-foreground"
            style={{ fontSize: "var(--text-stat)", lineHeight: 1, letterSpacing: "-0.04em" }}>
            {statValue}
          </p>
          {c.stat_label && <p className="text-base text-muted-foreground mt-2">{c.stat_label}</p>}
        </Reveal>
      )}
    </div>
  );

  return (
    <Section id="about" tone={tone}>
      <div className="grid md:grid-cols-[7fr_5fr] gap-12 md:gap-16 items-center">
        {imgRight ? textBlock : statBlock}
        {imgRight ? statBlock : textBlock}
      </div>
    </Section>
  );
}

function BentoSection({ content, tone }) {
  const c = content || {};
  const cards = c.cards || [];
  // slideWidth="full": pedido explícitamente — cada tarjeta de "Qué Hacemos"
  // pasa de ser una entre varias a un slide a toda la sección. El
  // desplazamiento de derecha a izquierda al avanzar ya lo da gratis el
  // carrusel compartido (scroll-snap + los botones/puntos existentes): con
  // una tarjeta por página, "siguiente" desplaza el carril exactamente su
  // ancho. Las demás secciones que usan CarouselSection (Eventos, Noticias,
  // Podcast) no llevan esta prop y no cambian.
  return (
    <CarouselSection label={c.label} title={c.title} tone={tone} slideWidth="full">
      {cards.map((card, i) => (
        <ServiceFlipCard key={i} card={card} index={i} full />
      ))}
    </CarouselSection>
  );
}

/**
 * A sangre y a pantalla completa, como el héroe de portada — Paco pidió
 * explícitamente que "cogiera toda la pantalla" en vez de ser una tarjeta
 * redondeada dentro de una sección normal. Por eso ya no lleva `Section` ni
 * `tone`: es estructural, igual que `hero`/`cta`/`banner` (ver
 * `SECCIONES_ESTRUCTURALES` más abajo).
 *
 * La foto del Evangelio del día es ahora el fondo a sangre, con el mismo
 * tratamiento que el héroe: `<img>` real con parallax, no `background-image`
 * de CSS. Sin foto, el degradado de siempre sigue siendo un fondo válido por
 * sí mismo — no hay hueco roto que rellenar.
 */
function WordOfDaySection({ wordOfDay, loading }) {
  const bgRef = useParallax(0.2);
  const imagen = wordOfDay?.image ? normalizeImgUrl(wordOfDay.image) : null;

  return (
    <section
      data-testid="word-of-day-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden text-white"
      style={{ background: "linear-gradient(150deg, #0b1f3a 0%, #061325 100%)" }}
    >
      {imagen && (
        <div ref={bgRef} className="absolute inset-0 will-change-transform">
          <img src={imagen} alt="" aria-hidden="true" decoding="async" className="w-full h-full object-cover" />
        </div>
      )}
      {/* El mismo degradado de siempre, ahora de velo sobre la foto en vez de
          ser el propio fondo: el texto se lee igual de bien haya o no foto. */}
      <div
        className="absolute inset-0"
        style={{
          background: imagen
            ? "linear-gradient(150deg, rgba(11,31,58,.90) 0%, rgba(6,19,37,.94) 100%)"
            : "transparent",
        }}
      />

      <div className="relative z-10 w-full max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c8a96e] mb-8 flex items-center justify-center gap-2">
          <Star className="w-3.5 h-3.5" aria-hidden="true" /> Palabra del Día
        </p>

        {loading && !wordOfDay ? (
          <div className="max-w-2xl mx-auto space-y-4">
            <Skeleton className="h-11 w-full bg-white/10" />
            <Skeleton className="h-11 w-5/6 mx-auto bg-white/10" />
            <Skeleton className="h-3 w-28 mx-auto bg-white/10 mt-6" />
          </div>
        ) : wordOfDay ? (
          <>
            <blockquote
              className="font-spiritual leading-tight mb-6 text-balance"
              style={{ fontSize: "clamp(32px, 5vw, 64px)" }}
            >
              {wordOfDay.text}
            </blockquote>
            <cite className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60 not-italic">
              {wordOfDay.reference}
            </cite>
            {wordOfDay.reflection && (
              <p className="mt-8 max-w-xl mx-auto text-base text-white/70 leading-relaxed border-t border-white/10 pt-8 line-clamp-3 whitespace-pre-line">
                {wordOfDay.reflection}
              </p>
            )}
            <CTAButton to="/palabra-del-dia" variant="gold" className="mt-10 font-semibold">
              Leer reflexión completa <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </CTAButton>
          </>
        ) : (
          <>
            <blockquote
              className="font-spiritual leading-tight mb-6 text-balance"
              style={{ fontSize: "clamp(32px, 5vw, 64px)" }}
            >
              Id por todo el mundo y proclamad el Evangelio a toda la creación
            </blockquote>
            <cite className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60 not-italic">Mc 16, 15</cite>
          </>
        )}
      </div>
    </section>
  );
}

function EventsSection({ events, loading, tone }) {
  const shown = events || [];
  // Estado del modal de inscripción interna, igual que en la página /eventos
  // (Eventos.js): esta sección es autónoma (no se comparte con esa página),
  // así que gestiona su propio estado en vez de subirlo a Home().
  const [enrollModal, setEnrollModal] = useState(null);

  if (loading && !shown.length) {
    return (
      <Section tone={tone}>
        <SectionHeading label="Agenda" title="Próximos Eventos" />
        <CardGridSkeleton count={3} />
      </Section>
    );
  }
  if (!shown.length) return null;

  return (
    <>
      {/* slideWidth="full": mismo tratamiento pedido para "Qué Hacemos" —
          cada evento pasa de ser una tarjeta entre varias a un slide a toda
          la sección. Las demás secciones que usan CarouselSection (Qué
          Hacemos aparte, Noticias, Podcast) no llevan esta prop y no
          cambian. */}
      <CarouselSection
        label="Agenda"
        title="Próximos Eventos"
        linkText="Ver todos"
        linkTo="/eventos"
        tone={tone}
        testId="events-section"
        maxItems={7}
        slideWidth="full"
      >
        {shown.map((ev, i) => (
          <Card key={ev._id || i} data-testid={`event-card-${i}`} interactive>
            <SmartImage
              src={ev.image}
              alt=""
              ratio="16 / 9"
              fallback={
                <div className="w-full h-full flex items-center justify-center bg-[#f5f5f7]">
                  <Calendar className="w-10 h-10 text-foreground/15" aria-hidden="true" />
                </div>
              }
            />
            <CardBody className="md:p-8">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-primary mb-3">
                {ev.category}
              </span>
              <h3 className="font-heading font-bold text-2xl md:text-3xl mb-3">{ev.title}</h3>
              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-2xl">{ev.description}</p>
              {/* Fecha/hora y CTA en una fila: en el modo "card" de siempre un
                  botón w-full tiene sentido en una tarjeta de ~360px, pero
                  aquí el slide ocupa toda la sección — se deja del ancho de
                  su contenido en vez de estirarlo. */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-border">
                <span className="text-sm text-muted-foreground">
                  {ev.date}{ev.time && ` · ${ev.time}`}
                </span>
                {ev.registration_url ? (
                  <CTAButton
                    href={ev.registration_url}
                    size="sm"
                    className="font-semibold"
                    data-testid={`event-register-${i}`}
                  >
                    Inscribirse <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                  </CTAButton>
                ) : (
                  <CTAButton
                    onClick={() => setEnrollModal({ id: ev.id || ev._id, title: ev.title, type: "evento" })}
                    size="sm"
                    className="font-semibold"
                    data-testid={`event-enroll-btn-${i}`}
                  >
                    Inscribirme <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </CTAButton>
                )}
              </div>
            </CardBody>
          </Card>
        ))}
      </CarouselSection>
      {enrollModal && (
        <EnrollmentModal
          itemId={enrollModal.id}
          itemTitle={enrollModal.title}
          itemType={enrollModal.type}
          onClose={() => setEnrollModal(null)}
        />
      )}
    </>
  );
}

function StatCard({ value, label, index }) {
  // El ref va en la cifra, que es lo que el observador vigila para arrancar
  // el conteo justo cuando entra en pantalla.
  const [ref, display] = useCountUp(value);
  return (
    <Card data-testid={`stat-item-${index}`} className="p-8 text-center" interactive>
      <p
        ref={ref}
        className="font-heading font-bold text-primary mb-2"
        style={{ fontSize: "clamp(40px, 5vw, 64px)", letterSpacing: "-0.03em", lineHeight: 1 }}
      >
        {display}
      </p>
      <p className="text-sm text-muted-foreground font-medium">{label}</p>
    </Card>
  );
}

function StatsSection({ content, stats, tone }) {
  const c = content || {};
  const items = [
    { value: stats?.reach || "50.000+", label: "Personas Alcanzadas" },
    { value: stats?.parishes || "85+", label: "Parroquias Acompañadas" },
    { value: `${stats?.resources ?? 4}+`, label: "Recursos Disponibles" },
    { value: `${stats?.courses ?? 3}+`, label: "Cursos Activos" },
  ];
  return (
    <Section tone={tone}>
      <Reveal>
        <SectionHeading label={c.label} title={c.title} />
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((s, i) => (
          <Reveal key={i} delay={i * 70} distance={28}>
            <StatCard index={i} value={s.value} label={s.label} />
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

function SocialSection({ content, tone }) {
  const c = content || {};
  const links = [
    { href: c.instagram || "https://instagram.com/evangelizaciondigitalhuelva", icon: Instagram, label: "Instagram", handle: "@evangelizaciondigitalhuelva" },
    { href: c.facebook || "https://facebook.com/profile.php?id=61579343082913", icon: Facebook, label: "Facebook", handle: "Evangelización Digital Huelva" },
    { href: c.youtube || "https://youtube.com/@EvangelizacionDigitalHuelva", icon: Youtube, label: "YouTube", handle: "@EvangelizacionDigitalHuelva" },
    { href: c.tiktok || "https://tiktok.com/@evangelizacion.di", icon: TikTok, label: "TikTok", handle: "@evangelizacion.di" },
  ];
  return (
    <Section tone={tone} width="narrow" innerClassName="max-w-5xl">
      <Reveal>
        <SectionHeading label={c.label} title={c.title} description={c.subtitle} />
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {links.map((s, i) => (
          <Reveal key={i} delay={i * 70} distance={24}>
            <a
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-card-${s.label.toLowerCase()}`}
              className="group relative rounded-3xl p-7 bg-[#1d1d1f] text-white card-hover focus-ring flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <s.icon className="w-5 h-5" size={20} aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-base">{s.label}</p>
                  <p className="text-sm text-[#86868b] truncate">{s.handle}</p>
                </div>
              </div>
              <span
                style={{ borderRadius: "var(--radius-pill)" }}
                className="flex-shrink-0 px-5 py-2 text-[13px] font-medium border border-white/30 group-hover:bg-white group-hover:text-black transition-colors"
              >
                Seguir
              </span>
            </a>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

function CTASection({ content }) {
  const c = content || {};
  return (
    <Section
      tone="dark"
      width="narrow"
      space="loose"
      data-testid="cta-missionary-section"
      innerClassName="text-center"
    >
      {c.label && (
        <Reveal distance={20}>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#86868b] mb-5">{c.label}</p>
        </Reveal>
      )}
      <Reveal delay={70} distance={36}>
        <h2 className="font-heading font-bold mb-6 text-balance"
          style={{ fontSize: "clamp(32px, 5vw, 56px)", lineHeight: 1.08, letterSpacing: "-0.03em" }}>
          {c.title}
        </h2>
      </Reveal>
      {c.description && (
        <Reveal delay={140} distance={24}>
          <p className="text-lg sm:text-xl text-[#a1a1a6] mb-10 leading-snug">{c.description}</p>
        </Reveal>
      )}
      {c.btn_text && (
        <Reveal delay={210} distance={20} className="flex justify-center">
          <CTAButton to={c.btn_url || "/misionero-digital"} variant="light" size="lg"
            data-testid="cta-join-missionary-btn">
            {c.btn_text} <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </CTAButton>
        </Reveal>
      )}
    </Section>
  );
}

function SeparatorSection({ content }) {
  const c = content || {};
  if (c.style === "fancy") {
    return (
      <div className="py-8 px-6 md:px-12 max-w-7xl mx-auto" aria-hidden="true">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-border" />
          <div className="w-2 h-2 rounded-full bg-primary" />
          <div className="flex-1 h-px bg-border" />
        </div>
      </div>
    );
  }
  return <div className="py-6" />;
}

function GallerySection({ content, tone }) {
  const c = content || {};
  const images = c.images || [];
  if (!images.length) return null;
  return (
    <Section tone={tone}>
      {c.title && (
        <Reveal>
          <SectionHeading title={c.title} />
        </Reveal>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((img, i) => (
          <Reveal key={i} delay={(i % 4) * 60} distance={24}>
            <SmartImage src={img.url} alt={img.alt || ""} ratio="1 / 1" rounded="rounded-2xl"
              className="card-hover" />
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

function VideoSection({ content, tone }) {
  const c = content || {};
  if (!c.url) return null;
  let embedUrl = c.url;
  if (c.url.includes("youtube.com/watch")) {
    try {
      const videoId = new URL(c.url).searchParams.get("v");
      if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } catch { /* URL malformada: se deja tal cual */ }
  } else if (c.url.includes("youtu.be/")) {
    const videoId = c.url.split("youtu.be/")[1]?.split("?")[0];
    if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
  }
  return (
    <Section tone={tone} width="reading">
      {c.title && (
        <Reveal>
          <SectionHeading title={c.title} description={c.description} align="center" />
        </Reveal>
      )}
      <Reveal delay={100} distance={30}>
        <div className="rounded-3xl overflow-hidden aspect-video shadow-rest">
          <iframe
            src={embedUrl}
            title={c.title || "Vídeo"}
            className="w-full h-full"
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      </Reveal>
    </Section>
  );
}

function QuotesSection({ content, tone }) {
  const c = content || {};
  return (
    <Section tone={tone} width="narrow" innerClassName="text-center">
      <Reveal distance={20}>
        <div className="font-spiritual leading-none mb-2" aria-hidden="true"
          style={{ fontSize: "clamp(80px, 12vw, 140px)", color: "rgba(0,113,227,0.15)" }}>
          “
        </div>
      </Reveal>
      <Reveal delay={80} distance={30}>
        <blockquote className="font-spiritual text-foreground leading-snug mb-6 text-balance"
          style={{ fontSize: "var(--text-quote)" }}>
          {c.text}
        </blockquote>
      </Reveal>
      <Reveal delay={160} distance={16}>
        {c.author && <cite className="text-sm font-semibold text-primary not-italic">{c.author}</cite>}
        {c.source && <p className="text-xs text-muted-foreground mt-1">{c.source}</p>}
      </Reveal>
    </Section>
  );
}

function BannerSection({ content }) {
  const c = content || {};
  return (
    <section
      className="px-6 md:px-12 relative overflow-hidden"
      style={{
        paddingBlock: "clamp(60px, 8vw, 120px)",
        backgroundImage: c.bg_image ? `url(${normalizeImgUrl(c.bg_image)})` : undefined,
        backgroundColor: c.bg_image ? undefined : (c.bg_color || "#1d1d1f"),
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {c.bg_image && <div className="absolute inset-0 bg-black/50" />}
      <div className="relative z-10 max-w-4xl mx-auto text-center" style={{ color: c.text_color || "#ffffff" }}>
        <Reveal distance={30}>
          {c.title && (
            <h2 className="font-heading font-bold mb-4 text-balance"
              style={{ fontSize: "var(--text-h2)", letterSpacing: "-0.03em" }}>
              {c.title}
            </h2>
          )}
          {c.subtitle && <p className="text-lg opacity-90">{c.subtitle}</p>}
        </Reveal>
      </div>
    </section>
  );
}

function LatestNewsSection({ content, latestNews, loading, tone }) {
  const c = content || {};
  const news = latestNews || [];
  const fmtDate = d =>
    d ? new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "";

  if (loading && !news.length) {
    return (
      <Section tone={tone}>
        <SectionHeading label="Actualidad" title={c.title || "Últimas Noticias"} />
        <CardGridSkeleton count={3} />
      </Section>
    );
  }
  if (!news.length) return null;

  return (
    <CarouselSection
      label="Actualidad"
      title={c.title || "Últimas Noticias"}
      description={c.description}
      linkText="Ver todas las noticias"
      linkTo="/noticias"
      tone={tone}
      maxItems={7}
    >
      {news.map(a => (
        <Card key={a._id} to={`/noticias/${a.slug}`}>
          <SmartImage
            src={a.image}
            alt=""
            ratio="16 / 9"
            fallback={
              <div className="w-full h-full flex items-center justify-center bg-[#f5f5f7]">
                <Newspaper className="w-10 h-10 text-foreground/15" aria-hidden="true" />
              </div>
            }
          />
          <CardBody>
            {fmtDate(a.published_date) && (
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">
                {fmtDate(a.published_date)}
              </p>
            )}
            <h3 className="font-heading font-bold text-base leading-snug mb-2 line-clamp-2">{a.title}</h3>
            {a.summary && <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{a.summary}</p>}
            <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-2 transition-[gap] duration-fast">
              Leer más <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </span>
          </CardBody>
        </Card>
      ))}
    </CarouselSection>
  );
}

function PodcastSection({ content, latestPodcast, loading, tone }) {
  const c = content || {};
  const episodes = latestPodcast || [];

  if (loading && !episodes.length) {
    return (
      <Section tone={tone}>
        <SectionHeading label={c.label} title={c.title || "Podcast"} />
        <CardGridSkeleton count={3} />
      </Section>
    );
  }
  if (!episodes.length) return null;

  return (
    <CarouselSection
      label={c.label}
      title={c.title || "Podcast"}
      description={c.description}
      linkText="Ver todos los episodios"
      linkTo="/podcast"
      tone={tone}
      maxItems={7}
    >
      {episodes.map((ep, i) => (
        <Card key={ep._id || i} href={ep.url}>
          <div className="relative">
            <SmartImage
              src={ep.image}
              alt=""
              ratio="16 / 9"
              fallback={
                <div className="w-full h-full flex items-center justify-center bg-[#f5f5f7]">
                  <Mic2 className="w-12 h-12 text-foreground/15" aria-hidden="true" />
                </div>
              }
            />
            {/* Indicador de reproducción. Decorativo: el enlace es la tarjeta
                entera, así que no debe ser un segundo destino tabulable. */}
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-base bg-black/30"
            >
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-xl">
                <Play className="w-6 h-6 text-primary fill-primary ml-1" />
              </div>
            </div>
          </div>
          <CardBody>
            {(ep.episode || ep.episode === 0) && (
              <p className="font-heading font-bold leading-none mb-3"
                style={{ color: "#0071e3", fontSize: "clamp(32px, 4vw, 44px)", letterSpacing: "-0.03em" }}>
                {ep.episode}
                <span className="text-sm font-medium text-muted-foreground tracking-normal ml-2">Episodio</span>
              </p>
            )}
            <h3 className="font-heading font-bold text-base leading-snug mb-2 line-clamp-2">{ep.title}</h3>
            {ep.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{ep.description}</p>}
            {ep.duration && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" aria-hidden="true" /> {ep.duration}
              </span>
            )}
          </CardBody>
        </Card>
      ))}
    </CarouselSection>
  );
}

// ─── Registro de secciones ────────────────────────────────────────────────────
const SECTION_RENDERERS = {
  hero: HeroSection,
  marquee: MarqueeSection,
  text_image: TextImageSection,
  bento: BentoSection,
  word_of_day: WordOfDaySection,
  events: EventsSection,
  stats: StatsSection,
  social: SocialSection,
  cta: CTASection,
  separator: SeparatorSection,
  gallery: GallerySection,
  video: VideoSection,
  quotes: QuotesSection,
  banner: BannerSection,
  latest_news: LatestNewsSection,
  podcast: PodcastSection,
};

// Secciones que no consumen turno en el ritmo de tonos: van a sangre o son
// puramente estructurales.
// `word_of_day` se suma aquí al hacerse a pantalla completa: ya no es una
// tarjeta dentro del ritmo claro/suave, es una sección a sangre como el
// héroe, así que tampoco debe consumir turno de tono.
const SECCIONES_ESTRUCTURALES = new Set(["hero", "marquee", "separator", "banner", "cta", "word_of_day"]);

// Portada mínima que se pinta mientras llega la respuesta del backend. No es
// un placeholder gris: es la cabecera real, con el mismo texto por defecto que
// ya usaba el renderizador. El visitante ve la web, no un spinner.
const SECCIONES_INICIALES = [
  { id: "hero-inicial", type: "hero", order: 0, content: {} },
];

/**
 * Ritmo de tonos por capítulos.
 *
 * Antes el fondo se derivaba de la paridad del índice, así que blanco y gris
 * se alternaban en TODAS las secciones y la página quedaba a rayas de arriba
 * abajo. Ahora se agrupa de dos en dos: dos claras, dos suaves. El contraste
 * fuerte queda para las secciones oscuras a sangre, que actúan de puntuación.
 */
function tonoPara(indice) {
  return Math.floor(indice / 2) % 2 === 0 ? "light" : "muted";
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function Home() {
  const { data, loading } = useCachedResource("home", cargarPortada);

  const lista = v => (Array.isArray(v) ? v : []);
  const seccionesCms = lista(data?.page?.sections);
  const secciones = seccionesCms.length ? seccionesCms : SECCIONES_INICIALES;
  const wordOfDay = data?.word_of_day ?? null;
  const stats = data?.stats ?? { reach: "50.000+", parishes: "85+" };
  const events = lista(data?.events);
  const latestNews = lista(data?.latest_articles);
  const latestPodcast = lista(data?.podcast);

  const ordenadas = [...secciones]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter(s => s.visible !== false);

  let indiceContenido = 0;

  return (
    <div data-testid="home-page">
      <SEO
        title="Evangelización Digital | Diócesis de Huelva"
        bare
        description="Delegación de Evangelización Digital de la Diócesis de Huelva. Llevamos el Evangelio a las redes, a las pantallas y a los corazones."
      />

      {ordenadas.map(section => {
        const Renderer = SECTION_RENDERERS[section.type];
        if (!Renderer) return null;

        const estructural = SECCIONES_ESTRUCTURALES.has(section.type);
        const tone = estructural ? undefined : tonoPara(indiceContenido++);

        return (
          <Renderer
            key={section.id}
            content={section.content}
            stats={stats}
            wordOfDay={wordOfDay}
            events={events}
            latestNews={latestNews}
            latestPodcast={latestPodcast}
            loading={loading}
            tone={tone}
          />
        );
      })}

      <FloatingEditButton slug="home" />
    </div>
  );
}
